import { Component, EventEmitter, Input, NgZone, OnChanges, OnInit, Output, ChangeDetectorRef, ViewChild } from '@angular/core';
import { AdditionalInformation, AdditionalInformationDescription } from '../../../models/enums/additionalInformation';
import { AdditionalInformationService } from '../../../services/additional-information/additional-information.service';
import { AdditionalInfo, GetAdditionalInformationResponse } from '../../../models/account/additional-information/getAdditionalInformationResponse';
import { MessageService } from 'primeng/api';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Store } from '@ngxs/store';
import { forkJoin } from 'rxjs';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TabViewModule } from 'primeng/tabview';
import { ActivatedRoute } from '@angular/router';
import { CalendarModule } from 'primeng/calendar';
import { FileSelectEvent, FileUpload, FileUploadModule } from 'primeng/fileupload';
import { DialogModule } from 'primeng/dialog';
import { UserSelectors } from '../../../states/user/user-selector';
import { UserStateModel } from '../../../states/user/user-state-model';
import { UpdateAdditionalInformationByMentorRequest } from '../../../models/account/additional-information/additionalInformationRequest';
import { ColorPickerModule } from 'primeng/colorpicker';

@Component({
  selector: 'app-additional-information-right-section',
  standalone: true,
  imports: [CommonModule, ButtonModule, ProgressSpinnerModule, TabViewModule,
    CalendarModule, FormsModule, ReactiveFormsModule, FileUploadModule, DialogModule
  ],
  templateUrl: './additional-information-right-section.component.html',
  styleUrl: './additional-information-right-section.component.scss'
})
export class AdditionalInformationRightSectionComponent implements OnInit {

  @Input() infoType!: AdditionalInformation;
  @Output() taskUpdated = new EventEmitter<void>();
  @ViewChild('fileUploader') fileUploader!: FileUpload;
  currentProfileSection: string | undefined;
  infoStatus: string = 'Pending';
  pendingQuestions: GetAdditionalInformationResponse | undefined;
  resolvedQuestions: GetAdditionalInformationResponse | undefined;
  visible: boolean = false;
  additionalInformationForm!: FormGroup;
  submitted: boolean = false;
  isLoadingData: boolean = false;
  isLoading: boolean = false;
  userId: string = '';
  mentor!: UserStateModel;
  currentIndex: number | undefined;
  attachment: File | Blob | undefined;
  brandColors: string[] = ['#000086', '#CDD12A', '#12263F', '#6766B6', '#B1C2D9'];
  selectedColor: string = '';

  constructor(private messageService: MessageService,
    private additionalInformationService: AdditionalInformationService,
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private store: Store,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.userId = this.route.snapshot.paramMap.get('id') || '';
    this.getMentor();
    this.loadData();
    this.additionalInformationService.getLoadDataObserrvable().subscribe(() =>{
      this.loadData();
    });
    this.currentProfileSection = AdditionalInformationDescription[this.infoType];
    this.initializeForm();

  }

  showDialog(id: number) {
    this.currentIndex = id;
    this.patchValuesToForm(id);
    this.visible = true;
  }
  closeDialog(){
    this.visible = false;
    this.currentIndex = undefined;
    this.attachment = undefined;
    this.selectedColor = '';
    this.fileUploader.clear();
    this.resetForm();
  }
  convertBlobToBase64(file: File | Blob | undefined): Promise<string>{
    return new Promise((resolve, reject) => {
      if(!file){
        reject('No file provided');
        return;
      }
      const reader = new FileReader();
      reader.readAsDataURL(file);

      reader.onload = () => {
        resolve(reader.result as string)
      }
      reader.onerror = (error) => {
        reject('Error reading file: '+ error);
      }
    })
  }
  onFileSelect($event: FileSelectEvent): void {
      if ($event.files && $event.files.length > 0) {
        // Convert files to an array
        const filesArray = Array.from($event.files);
        var index = 0;
        filesArray.forEach(item => {
          if (item.size > 1048576) { // 1 MB in bytes
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: `${item.name} file size can't exceed 1 MB`
            });
            filesArray.splice(index, 1);
            this.fileUploader.clear();
          }
          index++;
        });
        if (filesArray.length > 0) {
          this.attachment = filesArray[0];
        }
      }
    }

  onFileSelection($event: import("ngx-awesome-uploader").FilePreviewModel[]) {
    if ($event.length > 0) {
      this.attachment = $event[0].file
    }
  }

  onFileRemoved() {
    this.attachment = undefined;
    if (this.fileUploader) {
      this.fileUploader.clear();   // ✅ This clears the UI preview
    }
    this.cdr.detectChanges();      // Force UI refresh
  }

  initializeForm(){
    this.additionalInformationForm = this.formBuilder.group({
      additionalInfo: ['', Validators.required],
      dueDate: [null, Validators.required],
      startDate: [null, Validators.required],
      color: ['']
    });
  }

  patchValuesToForm(id: number) {
    if (this.pendingQuestions && this.pendingQuestions.additionalInfo.length > 0) {
      const info = this.pendingQuestions.additionalInfo.find(x => x.id === id);
      if (!info) return;

      this.additionalInformationForm.patchValue({
        additionalInfo: info.question,
        dueDate: new Date(info.dueDate || ''),
        startDate: new Date(info.startDate || ''),
        color: info.colourCode ?? ''
      });
      this.selectedColor = info.colourCode ?? '';
    }
  }

  onColorSelect(color: string): void {
    this.selectedColor = color;
    this.additionalInformationForm.get('color')?.setValue(color);
  }

  getMentor(){
    this.store.select(UserSelectors.getUser).subscribe({
      next: (response) => {
        this.mentor = response;
      },
      error: (error) => {
        console.warn('Error fetching user:', error);
      }
    });
  }

  resetForm() {
    this.submitted = false;
    this.additionalInformationForm.reset();
    this.additionalInformationForm.clearValidators();
  }

  // onUpdate(){
  //   this.submitted = true;
  //   this.isLoading = true;
  //   if (this.additionalInformationForm.invalid) {
  //     return;
  //   }
  //   const formValue = this.additionalInformationForm.value;
  //   const selectedInfo = this.pendingQuestions?.additionalInfo.find(x => x.id === this.currentIndex);

  //   this.convertBlobToBase64(this.attachment).then((result) => {
  //     const req: UpdateAdditionalInformationByMentorRequest = {
  //       studentId: this.userId,
  //       mentorId: this.mentor.id,
  //       type: this.infoType,
  //       additionalInformationId: selectedInfo!.id,
  //       question: formValue.additionalInfo,
  //       dueDate: formValue.dueDate,
  //       startDate: formValue.startDate,
  //       colourCode: formValue.color,
  //       requestedAttachment: result || null
  //     };
  //     this.additionalInformationService.updateAdditionalInformationByMentor(req).subscribe({
  //       next: () => {
  //         this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Additional information updated successfully' });
  //         this.visible = false;
  //         this.loadData();
  //         // this.submitted = false;
  //         this.isLoading = false;
  //         this.cdr.markForCheck();
  //       },
  //       error: (error) => {
  //         this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error updating additional information' });
  //         this.cdr.markForCheck();
  //       }
  //     });
  //   }).catch((error) => {
  //     this.isLoading = false;
  //   })

  // }
  onUpdate() {
    this.submitted = true;
    this.isLoading = true;
    if (this.additionalInformationForm.invalid) {
      this.isLoading = false;
      return;
    }

    const formValue = this.additionalInformationForm.value;
    const selectedInfo = this.pendingQuestions?.additionalInfo.find(x => x.id === this.currentIndex);

    const processRequest = (attachmentBase64: string | null) => {
      const req: UpdateAdditionalInformationByMentorRequest = {
        studentId: this.userId,
        mentorId: this.mentor.id,
        type: this.infoType,
        additionalInformationId: selectedInfo!.id,
        question: formValue.additionalInfo,
        dueDate: formValue.dueDate,
        startDate: formValue.startDate,
        colourCode: formValue.color,
        requestedAttachment: attachmentBase64
      };

      this.additionalInformationService.updateAdditionalInformationByMentor(req).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Task information updated successfully'
          });
          this.visible = false;
          this.attachment = undefined;
          this.fileUploader.clear();
          this.loadData();
          this.isLoading = false;
          this.cdr.markForCheck();
          this.taskUpdated.emit();
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Error updating task information'
          });
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
    };

    if (this.attachment) {
      this.convertBlobToBase64(this.attachment).then((result) => {
        processRequest(result);
      }).catch(() => {
        this.isLoading = false;
      });
    } else {
      processRequest(null);
    }
  }


  loadData() {
    this.isLoadingData = true;
    return forkJoin({
      pendingQuestions: this.additionalInformationService.getAdditionalInformation({
        studentId: this.userId,
        type: this.infoType,
        status: 'Pending'
      }),
      resolvedQuestions: this.additionalInformationService.getAdditionalInformation({
        studentId: this.userId,
        type: this.infoType,
        status: 'Submitted'
      }), // ✅ No empty string
    }).subscribe({
      next: response => {
        if (response != null) {
          this.pendingQuestions = response.pendingQuestions;
          this.resolvedQuestions = response.resolvedQuestions;
        }
        this.isLoadingData = false;
        this.cdr.markForCheck();
      },
      error: (error) => {
      this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error fetching additionalInfo' });
      this.isLoadingData = false;
      this.cdr.markForCheck();
      }
    });
  }


  openAttachment(fileUrl: string | undefined) {
    if (fileUrl) {
      window.open(fileUrl, '_blank');
    }
  }
}
